import { SHIPPING_STATUS_EVENT } from '@theme/shipping-calculator';

/**
 * @param {Element | null} section
 * @returns {{
 *   productForm: Element | null,
 *   buttons: Element | null,
 *   notifyWrapper: Element | null,
 *   addToCartContainers: NodeListOf<Element>,
 *   acceleratedCheckout: Element | null,
 *   stickyBar: Element | null,
 *   quantityWrapper: Element | null,
 * }}
 */
function getShippingUi(section) {
  const productForm = section?.querySelector('product-form-component') || null;
  const buttons = productForm?.querySelector('.product-form-buttons') || null;

  return {
    productForm,
    buttons,
    notifyWrapper: buttons?.querySelector('[data-shipping-notify]') || null,
    addToCartContainers: buttons?.querySelectorAll('add-to-cart-component') || [],
    acceleratedCheckout: buttons?.querySelector('.shopify-payment-button') || null,
    stickyBar: section?.querySelector('sticky-add-to-cart') || null,
    quantityWrapper: buttons?.querySelector('.quantity-selector-wrapper') || null,
  };
}

/**
 * @param {NodeListOf<Element> | Element[]} containers
 * @param {boolean} disabled
 */
function setAddToCartDisabled(containers, disabled) {
  containers.forEach((container) => {
    const button = container.querySelector('button');
    if (!(button instanceof HTMLButtonElement)) return;

    if (disabled) {
      button.disabled = true;
      button.setAttribute('data-shipping-pending', '');
      return;
    }

    button.removeAttribute('data-shipping-pending');
    if (!button.hasAttribute('data-native-disabled')) {
      button.disabled = false;
    }
  });
}

/**
 * @param {Element | null} buttons
 * @param {Element | null} productForm
 * @param {'loading' | 'available' | 'unavailable' | 'missing_cep'} status
 */
function setShippingStatus(buttons, productForm, status) {
  buttons?.setAttribute('data-shipping-status', status);
  productForm?.setAttribute('data-shipping-status', status);

  if (status === 'available') {
    productForm?.removeAttribute('data-shipping-blocked');
  } else {
    productForm?.setAttribute('data-shipping-blocked', 'true');
  }
}

/**
 * @param {CustomEvent} event
 */
function handleShippingStatus(event) {
  const detail = event.detail;
  if (!detail) return;

  const section = document.querySelector('.product-information');
  if (!section) return;

  const {
    productForm,
    buttons,
    notifyWrapper,
    addToCartContainers,
    acceleratedCheckout,
    stickyBar,
  } = getShippingUi(section);

  const setButtonsLoading = (loading) => {
    addToCartContainers.forEach((container) => {
      const button = container.querySelector('button');
      if (!(button instanceof HTMLButtonElement)) return;

      if (loading) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.classList.add('shipping-calculator__button--loading');
      } else {
        button.removeAttribute('aria-busy');
        button.classList.remove('shipping-calculator__button--loading');
      }
    });

    const stickyButton = stickyBar?.querySelector('[ref="addToCartButton"]');
    if (stickyButton instanceof HTMLButtonElement) {
      stickyButton.toggleAttribute('aria-busy', loading);
    }
  };

  switch (detail.status) {
    case 'loading':
      setButtonsLoading(true);
      setAddToCartDisabled(addToCartContainers, true);
      setShippingStatus(buttons, productForm, 'loading');
      notifyWrapper?.setAttribute('hidden', '');
      acceleratedCheckout?.setAttribute('hidden', '');
      stickyBar?.removeAttribute('hidden');
      break;

    case 'available':
      setButtonsLoading(false);
      setAddToCartDisabled(addToCartContainers, false);
      setShippingStatus(buttons, productForm, 'available');
      notifyWrapper?.setAttribute('hidden', '');
      acceleratedCheckout?.removeAttribute('hidden');
      stickyBar?.removeAttribute('hidden');
      break;

    case 'unavailable':
      setButtonsLoading(false);
      setAddToCartDisabled(addToCartContainers, true);
      setShippingStatus(buttons, productForm, 'unavailable');
      notifyWrapper?.removeAttribute('hidden');
      acceleratedCheckout?.setAttribute('hidden', '');
      stickyBar?.setAttribute('hidden', '');
      break;

    case 'missing_cep':
      setButtonsLoading(false);
      setAddToCartDisabled(addToCartContainers, true);
      setShippingStatus(buttons, productForm, 'missing_cep');
      notifyWrapper?.setAttribute('hidden', '');
      acceleratedCheckout?.setAttribute('hidden', '');
      stickyBar?.removeAttribute('hidden');
      break;
  }

  const stickyButton = stickyBar?.querySelector('[ref="addToCartButton"]');
  if (stickyButton instanceof HTMLButtonElement) {
    if (detail.status === 'available') {
      stickyButton.disabled = stickyButton.hasAttribute('data-native-disabled');
      stickyButton.removeAttribute('data-shipping-pending');
    } else {
      stickyButton.disabled = true;
      stickyButton.setAttribute('data-shipping-pending', '');
    }
  }
}

function initProductShippingGate() {
  const section = document.querySelector('.product-information');
  if (!section?.querySelector('shipping-calculator')) return;

  const { productForm, buttons, addToCartContainers, stickyBar } = getShippingUi(section);
  const stickyButton = stickyBar?.querySelector('[ref="addToCartButton"]');

  setAddToCartDisabled(addToCartContainers, true);
  setShippingStatus(buttons, productForm, 'loading');

  if (stickyButton instanceof HTMLButtonElement && !stickyButton.hasAttribute('data-native-disabled')) {
    stickyButton.disabled = true;
    stickyButton.setAttribute('data-shipping-pending', '');
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductShippingGate);
} else {
  initProductShippingGate();
}

document.addEventListener(SHIPPING_STATUS_EVENT, handleShippingStatus);
